import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import MockCaseTimeline from "../MockCaseTimeLine";
import { InterviewContext } from "../../../contexts/InterviewContext";
import InterviewCompleteDialog from "./dialogs/InterviewCompleteDialog";
import SkipThisQuestionDialog from "./dialogs/SkipThisQuestionDialog";
import IdleErrorDialog from "./dialogs/IdleErrorDialog";
import { useLoading } from "src/contexts/LoadingContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import { useAuth } from "src/contexts/AuthContext";
import UserOnboarding from "../Tips/UserOnboarding";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";

// New components
import InterviewerDisplay from "./InterviewerDisplay";
import GraphCarousel from "./GraphCarousel";
import IntervieweeCamera from "./IntervieweeCamera";
import InterviewControls from "./InterviewControls";
import useCameraStream from "./hooks/useCameraStream";
import useGraphNavigation from "./hooks/useGraphNavigation";
import { TutorialStep, useTutorial } from "../Tutorial/TutorialContext";

const MockCaseMain: React.FC = () => {
  // Context values
  const {
    isInterviewerSpeaking,
    caseQuestionNumber,
    graphs,
    isEnd,
    sessionId,
    currentQuestionNumber,
    endInterviewHandler,
    isFirstTimeUser,
    questionTypes,
  } = useContext(InterviewContext)!;

  const { firebaseUser, userDoc } = useAuth();
  const { handleTimeBasedLoading } = useLoading();
  const navigate = usePartnerAwareNavigate();

  // Local state
  const [isInterviewerThinking, setIsInterviewerThinking] = useState(false);
  const [interviewEnded, setInterviewEnded] = useState(false);
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [showIdleErrorDialog, setShowIdleErrorDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  // Custom hooks
  const { videoRef, cameraActive, hasPermission, toggleCamera } =
    useCameraStream();
  const { storedGraphs, currentGraphIndex, hasGraphs, nextGraph, prevGraph } =
    useGraphNavigation({ graphs, currentQuestionNumber });
  const {
    isActive: isTutorialActive,
    currentStep: tutorialStep,
    isAwaitingAction,
    completeAction,
  } = useTutorial();
  useEffect(() => {
    console.log("isTutorialActive", isTutorialActive);
    console.log("tutorialStep", tutorialStep);
    console.log("isAwaitingAction", isAwaitingAction);
  }, [isTutorialActive, tutorialStep, isAwaitingAction]);
  const isInteractiveTutorialStep =
    isTutorialActive &&
    tutorialStep === TutorialStep.MICROPHONE_INTERACTIVE &&
    isAwaitingAction;

  // Track interviewer speaking/thinking state
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isInterviewerSpeaking) {
      setIsInterviewerThinking(true);
    } else {
      timeoutId = setTimeout(() => {
        setIsInterviewerThinking(false);
      }, 700);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isInterviewerSpeaking]);

  // Watch for user idle status
  useEffect(() => {
    if (userDoc && userDoc.status === "error-idle") {
      handleInterviewEnd(true);
    }
  }, [userDoc]);

  // Handle page refresh warning - REMOVED as it's now handled in the parent component
  const { interviewSessionId } = useParams();

  // Button handlers
  const handleSkipQuestion = () => {
    console.log("Skipping question...");
    setShowSkipDialog(true);
    setIsInterviewerThinking(true);
  };

  const handleFinishTalking = async () => {
    console.log("finish talking...");

    setIsInterviewerThinking(true);
    console.log("isAwaitingAction", isAwaitingAction);
    if (isAwaitingAction) {
      completeAction("microphone");
    }
  };
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Report generation
  const generateWholeReport = httpsCallable(functions, "generateWholeReport");
  const generateReport = async (sessionId: string, uid: string) => {
    console.log("Generating report...");
    try {
      // Set a longer timeout (optional, if supported by your Firebase plan)
      return await generateWholeReport({ sessionId, uid });
    } catch (error) {
      console.error("Error generating report:", error);
      throw error; // Re-throw to handle in the caller
    }
  };

  // Navigation handlers
  //   const handleViewReport = useCallback(() => {
  //     if (debounceTimer.current) {
  //       clearTimeout(debounceTimer.current);
  //     }

  //     debounceTimer.current = setTimeout(async () => {
  //       setShowCompleteDialog(false);
  //       generateReport(sessionId, firebaseUser?.uid!);
  //       navigate("/");
  //     }, 3000);
  //   }, [firebaseUser?.uid, sessionId, navigate, handleTimeBasedLoading]);

  const handleReturnHome = () => {
    navigate("/");
    window.location.reload();
  };

  // Interview ending
  async function handleInterviewEnd(error = false) {
    try {
      setInterviewEnded(true);
      await endInterviewHandler(firebaseUser?.uid!);

      // Add delay before showing dialog
      setTimeout(() => {
        if (!error) setShowCompleteDialog(true);
        else setShowIdleErrorDialog(true);
      }, 6500); // 3 second delay
    } catch (error) {
      console.error("Failed to end interview:", error);
    }
  }

  useEffect(() => {
    if (isEnd && !interviewEnded) {
      handleInterviewEnd();
    }
  }, [isEnd, interviewEnded, firebaseUser]);

  const controlsActive =
    !isInterviewerSpeaking &&
    !isInterviewerThinking &&
    (!isFirstTimeUser || isInteractiveTutorialStep);

  return (
    <>
      <div
        className="flex-1 flex flex-col relative  shadow-md rounded-md mx-6 mb-6"
        style={{
          background:
            "linear-gradient(0deg, rgba(66, 62, 216, 0.08) 0%, rgba(237, 237, 237, 0.08) 100%)",
        }}
      >
        <div className="w-full m-0 p-0 flex justify-between">
          <MockCaseTimeline
            currentStep={currentQuestionNumber + 1}
            totalSteps={caseQuestionNumber + 1}
            questionTypes={questionTypes}
          />
        </div>
        <div className="flex-1 relative flex items-center justify-center">
          {!hasGraphs ? (
            // No graphs layout - show full interviewer
            <InterviewerDisplay isInterviewerSpeaking={isInterviewerSpeaking} />
          ) : (
            // Has graphs layout - show compact interviewer + graphs
            <>
              <div className="absolute top-14 left-6 flex flex-col z-50 items-center">
                <InterviewerDisplay
                  isInterviewerSpeaking={isInterviewerSpeaking}
                  isCompact={true}
                />
              </div>

              {/* Graph Carousel */}
              <div className="flex-1 flex items-center justify-center z-50">
                <GraphCarousel
                  graphs={storedGraphs}
                  currentIndex={currentGraphIndex}
                  onNext={nextGraph}
                  onPrev={prevGraph}
                />
              </div>
            </>
          )}

          {/* Controls */}
          <InterviewControls
            onCameraToggle={toggleCamera}
            onMicrophoneClick={handleFinishTalking}
            onSkipQuestion={handleSkipQuestion}
            isActive={controlsActive}
            isTutorial={isInteractiveTutorialStep}
          />

          {/* Interviewee Camera */}
          <IntervieweeCamera
            username={userDoc ? userDoc.survey?.name : "WeKruiter"}
            isInterviewerSpeaking={isInterviewerSpeaking}
            isInterviewerThinking={isInterviewerThinking}
          />
        </div>
      </div>

      {/* Dialogs */}
      {showCompleteDialog && (
        <InterviewCompleteDialog
          isOpen={showCompleteDialog}
          onClose={() => setShowCompleteDialog(false)}
          onViewReport={handleReturnHome}
          onReturnHome={handleReturnHome}
        />
      )}
      {showSkipDialog && (
        <SkipThisQuestionDialog
          isOpen={showSkipDialog}
          onOpenChange={setShowSkipDialog}
        />
      )}
      {showIdleErrorDialog && (
        <IdleErrorDialog
          isOpen={showIdleErrorDialog}
          onOpenChange={setShowIdleErrorDialog}
          onReturnHome={handleReturnHome}
        />
      )}
    </>
  );
};

export default MockCaseMain;
